<template>
	<section
		class="settings-section organization-section new-settings__section"
		aria-labelledby="organization-section-title"
	>
		<header class="new-settings__section-header">
			<div class="new-settings__section-header-row">
				<div class="new-settings__section-header-text">
					<div class="new-settings__section-title-row">
						<h1
							id="organization-section-title"
							class="new-settings__section-title"
						>
							{{ strings.title }}
							<span v-if="profileTitleSuffix">
								-
								<ClippedText
									:text="profileTitleSuffix"
									:length="50"
								/>
							</span>
						</h1>
						<OrganizationProfileStatusBadge
							v-if="profileStatus"
							:status="profileStatus"
						/>
					</div>
					<p class="new-settings__section-subtitle">
						{{ strings.subtitle }}
					</p>
				</div>
			</div>
		</header>

		<div class="settings-section__content">
			<engage-loader v-if="isOrganizationLoading" overlay />

			<OrganizationProfileStatusBanner
				:status="profileStatus"
				:meta="profileStatusMeta"
			/>

			<div
				class="settings-section__card organization-section__admins-card"
			>
				<div class="settings-section__card-body">
					<div class="organization-section__admins-header">
						<div class="organization-section__admins-title-row">
							<h2 class="settings-section__card-title">
								{{ strings.adminsCard.title }}
							</h2>
							<span class="organization-section__view-only-badge">
								{{ strings.adminsCard.viewOnlyBadge }}
							</span>
						</div>
					</div>

					<p class="organization-section__admins-description">
						{{ strings.adminsCard.description }}
					</p>

					<div class="organization-section__admins-grid">
						<div
							v-for="admin in orgAdmins"
							:key="admin.id"
							class="organization-section__admin-card"
						>
							<p class="organization-section__admin-role">
								{{ admin.roleLabel }}
							</p>
							<div class="organization-section__admin-body">
								<div
									class="organization-section__admin-avatar"
									:class="
										admin.isPrimary
											? 'organization-section__admin-avatar--primary'
											: 'organization-section__admin-avatar--secondary'
									"
									aria-hidden="true"
								>
									{{ getAdminInitials(admin.name) }}
								</div>
								<div class="organization-section__admin-info">
									<p class="organization-section__admin-name">
										{{ admin.name }}
									</p>
									<p class="organization-section__admin-meta">
										{{ admin.meta }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<form
				class="organization-section__form-wrap"
				@submit.prevent="onSubmitForApproval"
			>
				<div class="settings-section__card">
					<div class="settings-section__card-header">
						<h2 class="settings-section__card-title">
							{{ strings.businessIdentityCardTitle }}
						</h2>
						<div
							v-if="!isOrganizationLoading"
							class="settings-section__card-actions"
						>
							<template v-if="isEditing">
								<div class="btn-pill-group">
									<button
										type="button"
										class="btn-pill btn-pill--sm"
										@click="cancelEditing"
									>
										{{ common.cancel }}
									</button>
									<button
										type="button"
										class="btn-pill btn-pill--sm"
										:disabled="
											isSaveDisabled ||
												isOrganizationSaving
										"
										@click="onSaveDraft"
									>
										{{ strings.actions.saveDraft }}
									</button>
									<button
										type="submit"
										class="btn-pill btn-pill--sm"
										:disabled="isOrganizationSaving"
									>
										{{ strings.actions.submitForApproval }}
									</button>
								</div>
							</template>
							<button
								v-else-if="!isFormLocked"
								type="button"
								class="btn-pill btn-pill--sm"
								@click="startEditing"
							>
								{{ common.edit }}
							</button>
						</div>
					</div>

					<div class="settings-section__card-body">
						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-legal-business-name"
									class="settings-section__label"
								>
									{{ strings.fields.legalBusinessName }}
								</label>
								<div
									v-if="
										isOrganizationFieldValueLocked(
											'legalBusinessName'
										)
									"
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ form.legalBusinessName }}
								</div>
								<div
									v-else
									class="settings-section__input-clear-wrap"
								>
									<input
										id="org-legal-business-name"
										v-model="form.legalBusinessName"
										type="text"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
										}"
										:readonly="!isEditing"
									/>
									<button
										v-if="
											isEditing && form.legalBusinessName
										"
										type="button"
										class="settings-section__input-clear"
										aria-label="Clear legal business name"
										@click="
											clearOrganizationField(
												'legalBusinessName'
											)
										"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-business-identity"
									class="settings-section__label"
								>
									{{ strings.fields.businessIdentity }}
								</label>
								<div
									id="org-business-identity"
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ form.businessIdentity }}
								</div>
							</div>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-business-type"
									class="settings-section__label"
								>
									{{ strings.fields.businessType }}
								</label>
								<div class="settings-section__select-wrap">
									<select
										id="org-business-type"
										v-model="form.businessType"
										class="settings-section__select"
										:class="{
											'settings-section__select--readonly': !isEditing,
										}"
										:tabindex="isEditing ? 0 : -1"
									>
										<option value="" disabled>
											{{ strings.selectBusinessType }}
										</option>
										<option
											value="Sole Proprietorship"
										>
											Sole Proprietorship
										</option>
										<option value="Partnership">
											Partnership
										</option>
										<option
											value="Limited Liability Corporation"
										>
											Limited Liability Corporation
										</option>
										<option value="Co-operative">
											Co-operative
										</option>
										<option
											value="Non-profit Corporation"
										>
											Non-profit Corporation
										</option>
										<option value="Corporation">
											Corporation
										</option>
									</select>
									<font-awesome-icon
										class="settings-section__select-icon"
										:icon="['far', 'chevron-down']"
										aria-hidden="true"
									/>
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-business-industry"
									class="settings-section__label"
								>
									{{ strings.fields.businessIndustry }}
								</label>
								<div
									id="org-business-industry"
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ form.businessIndustry }}
								</div>
							</div>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-place-of-incorporation"
									class="settings-section__label"
								>
									{{ strings.fields.placeOfIncorporation }}
								</label>
								<div class="settings-section__select-wrap">
									<select
										id="org-place-of-incorporation"
										v-model="form.placeOfIncorporation"
										class="settings-section__select"
										:class="{
											'settings-section__select--readonly': !isEditing,
										}"
										:tabindex="isEditing ? 0 : -1"
									>
										<option value="" disabled>
											{{
												strings.selectPlaceOfIncorporation
											}}
										</option>
										<option
											v-for="option in stateProvinceOptions"
											:key="option.value"
											:value="option.value"
										>
											{{ option.label }}
										</option>
									</select>
									<font-awesome-icon
										class="settings-section__select-icon"
										:icon="['far', 'chevron-down']"
										aria-hidden="true"
									/>
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-registration-id-type"
									class="settings-section__label"
								>
									{{
										strings.fields
											.businessRegistrationIdType
									}}
								</label>
								<div
									id="org-registration-id-type"
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ form.businessRegistrationIdType }}
								</div>
							</div>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-registration-id"
									class="settings-section__label"
								>
									{{
										strings.fields.businessRegistrationIdEin
									}}
								</label>
								<div class="settings-section__input-clear-wrap">
									<input
										id="org-registration-id"
										v-model="form.businessRegistrationId"
										type="text"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
										}"
										:placeholder="
											placeholders.businessRegistrationId
										"
										:readonly="!isEditing"
										inputmode="numeric"
										maxlength="10"
										@input="onBusinessRegistrationIdInput"
									/>
									<button
										v-if="
											isEditing &&
												form.businessRegistrationId
										"
										type="button"
										class="settings-section__input-clear"
										aria-label="Clear business registration ID"
										@click="
											clearOrganizationField(
												'businessRegistrationId'
											)
										"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-business-regions"
									class="settings-section__label"
								>
									{{
										strings.fields
											.businessRegionsOfOperation
									}}
								</label>
								<div
									id="org-business-regions"
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ form.businessRegionsOfOperation }}
								</div>
							</div>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-business-website"
									class="settings-section__label"
								>
									{{ strings.fields.businessWebsite }}
								</label>
								<div class="settings-section__input-clear-wrap">
									<input
										id="org-business-website"
										v-model="form.businessWebsite"
										type="text"
										inputmode="url"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
											'settings-section__input--error':
												businessWebsiteErrorMessage,
										}"
										:placeholder="
											placeholders.businessWebsite
										"
										:readonly="!isEditing"
										:aria-invalid="
											Boolean(businessWebsiteErrorMessage)
										"
										@blur="onBusinessWebsiteBlur"
									/>
									<button
										v-if="isEditing && form.businessWebsite"
										type="button"
										class="settings-section__input-clear"
										aria-label="Clear business website"
										@click="
											clearOrganizationField(
												'businessWebsite'
											)
										"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
								<p
									v-if="businessWebsiteErrorMessage"
									class="settings-section__error"
								>
									{{ businessWebsiteErrorMessage }}
								</p>
							</div>
						</div>

						<div class="organization-section__card-divider">
							<h3 class="organization-section__card-subtitle">
								{{ strings.headquartersAddressTitle }}
							</h3>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-address-1"
									class="settings-section__label"
								>
									{{ strings.fields.addressLine1 }}
								</label>
								<div class="settings-section__input-clear-wrap">
									<input
										id="org-address-1"
										v-model="form.address1"
										type="text"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
										}"
										:placeholder="placeholders.addressLine1"
										:readonly="!isEditing"
									/>
									<button
										v-if="isEditing && form.address1"
										type="button"
										class="settings-section__input-clear"
										:aria-label="aria.clearAddressLine1"
										@click="
											clearOrganizationField('address1')
										"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-address-2"
									class="settings-section__label"
								>
									{{ strings.fields.addressLine2Optional }}
								</label>
								<div class="settings-section__input-clear-wrap">
									<input
										id="org-address-2"
										v-model="form.address2"
										type="text"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
										}"
										:placeholder="placeholders.addressLine2"
										:readonly="!isEditing"
									/>
									<button
										v-if="isEditing && form.address2"
										type="button"
										class="settings-section__input-clear"
										:aria-label="aria.clearAddressLine2"
										@click="
											clearOrganizationField('address2')
										"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
							</div>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-city"
									class="settings-section__label"
								>
									{{ strings.fields.city }}
								</label>
								<div class="settings-section__input-clear-wrap">
									<input
										id="org-city"
										v-model="form.city"
										type="text"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
										}"
										:placeholder="placeholders.city"
										:readonly="!isEditing"
									/>
									<button
										v-if="isEditing && form.city"
										type="button"
										class="settings-section__input-clear"
										:aria-label="aria.clearCity"
										@click="clearOrganizationField('city')"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-state"
									class="settings-section__label"
								>
									{{ strings.fields.stateProvince }}
								</label>
								<div class="settings-section__select-wrap">
									<select
										id="org-state"
										v-model="form.stateProvince"
										class="settings-section__select"
										:class="{
											'settings-section__select--readonly': !isEditing,
										}"
										:tabindex="isEditing ? 0 : -1"
									>
										<option value="" disabled>
											{{ strings.selectStateProvince }}
										</option>
										<option
											v-for="option in stateProvinceOptions"
											:key="option.value"
											:value="option.value"
										>
											{{ option.label }}
										</option>
									</select>
									<font-awesome-icon
										class="settings-section__select-icon"
										:icon="['far', 'chevron-down']"
										aria-hidden="true"
									/>
								</div>
							</div>
						</div>

						<div class="settings-section__field-row">
							<div class="settings-section__field">
								<label
									for="org-country"
									class="settings-section__label"
								>
									{{ strings.fields.country }}
								</label>
								<div
									id="org-country"
									class="settings-section__readonly settings-section__readonly--highlight"
								>
									{{ form.country }}
								</div>
							</div>
							<div class="settings-section__field">
								<label
									for="org-postal-code"
									class="settings-section__label"
								>
									{{ strings.fields.postalCode }}
								</label>
								<div class="settings-section__input-clear-wrap">
									<input
										id="org-postal-code"
										v-model="form.postalCode"
										type="text"
										class="settings-section__input"
										:class="{
											'settings-section__input--clearable': isEditing,
											'settings-section__input--readonly': !isEditing,
										}"
										autocomplete="postal-code"
										:placeholder="placeholders.postalCode"
										:readonly="!isEditing"
										inputmode="numeric"
										maxlength="5"
										@input="onPostalCodeInput"
									/>
									<button
										v-if="isEditing && form.postalCode"
										type="button"
										class="settings-section__input-clear"
										:aria-label="aria.clearPostalCode"
										@click="
											clearOrganizationField('postalCode')
										"
									>
										<font-awesome-icon
											:icon="['fal', 'times']"
										/>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>
	</section>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapState } from 'vuex'

import ClippedText from '@/components/ClippedText.vue'
import EngageLoader from '@/components/EngageLoader.vue'
import {
	ORGANIZATION_PROFILE_STATUSES,
	ORGANIZATION_STATE_PROVINCE_OPTIONS,
	ORGANIZATION_STRINGS,
	SETTINGS_ARIA,
	SETTINGS_COMMON,
	SETTINGS_PLACEHOLDERS,
	SETTINGS_VALIDATION,
} from '@/config/settingsConstants'
import OrganizationProfileStatusBadge from '@/views/Settings/components/OrganizationProfileStatusBadge.vue'
import OrganizationProfileStatusBanner from '@/views/Settings/components/OrganizationProfileStatusBanner.vue'

/**
 * Resolve organization profile from API payload.
 *
 * @param {Object} data
 * @return {Object}
 */
const getOrganizationProfile = (data = {}) => data.organization_profile || data

/**
 * Normalize API null/undefined values to empty form strings.
 *
 * @param {*} value
 * @return {String}
 */
const toFormValue = value => (value == null ? '' : String(value))

/**
 * Format a business registration ID (EIN) as xx-xxxxxxx.
 *
 * @param {*} value
 * @return {String}
 */
const formatBusinessRegistrationId = value => {
	const digits = String(value || '')
		.replace(/\D/g, '')
		.slice(0, 9)

	if (digits.length <= 2) {
		return digits
	}

	return `${digits.slice(0, 2)}-${digits.slice(2)}`
}

/**
 * Format a business registration ID for the API payload as xx-xxxxxxx.
 *
 * @param {*} value
 * @return {String|null}
 */
const toBusinessRegistrationIdPayload = value =>
	formatBusinessRegistrationId(value) || null

/**
 * Restrict a postal code to 5 digits.
 *
 * @param {*} value
 * @return {String}
 */
const formatPostalCode = value =>
	String(value || '')
		.replace(/\D/g, '')
		.slice(0, 5)

const BUSINESS_WEBSITE_PATTERN = /^www\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/i

/**
 * Normalize a business website to host-only form (no https).
 *
 * @param {*} value
 * @return {String}
 */
const normalizeBusinessWebsite = value => {
	let website = String(value || '').trim()

	website = website.replace(/^https?:\/\//i, '')
	website = website.split('/')[0]

	return website
}

/**
 * Whether a business website is empty or matches www.xxx.yyy.
 *
 * @param {*} value
 * @return {Boolean}
 */
const isValidBusinessWebsite = value => {
	const website = normalizeBusinessWebsite(value)

	if (!website) {
		return true
	}

	return BUSINESS_WEBSITE_PATTERN.test(website)
}

const ORGANIZATION_REQUIRED_SUBMIT_FIELDS = [
	'legalBusinessName',
	'businessIdentity',
	'businessType',
	'businessIndustry',
	'placeOfIncorporation',
	'businessRegistrationIdType',
	'businessRegistrationId',
	'businessRegionsOfOperation',
	'businessWebsite',
	'address1',
	'city',
	'stateProvince',
	'country',
	'postalCode',
]

/**
 * Whether any required organization submit field is empty.
 *
 * @param {Object} form
 * @return {Boolean}
 */
const hasEmptyRequiredOrganizationFields = (form = {}) =>
	ORGANIZATION_REQUIRED_SUBMIT_FIELDS.some(
		field => !String(form[field] || '').trim()
	)

/**
 * Map organization API payload to OrganizationSection form values.
 *
 * API fields:
 * name, business_name, business_identity, business_type, business_industry,
 * business_registration_id, business_registration_type, business_regions,
 * business_website, incorporation_place, address_one, address_two, city,
 * state, postal_code, country, primary_business_title, primary_job_postition,
 * primary_phone
 *
 * @param {Object} params
 * @param {Object} [params.profile={}]
 * @param {Object} [params.data={}]
 * @param {String} [params.organizationName='']
 * @return {Object}
 */
const mapOrganizationToForm = ({
	profile = {},
	data = {},
	organizationName = '',
} = {}) => {
	const source = { ...profile, ...data }

	return {
		profileFriendlyName: toFormValue(source.name),
		legalBusinessName: toFormValue(
			source.business_name || organizationName || ''
		),
		businessIdentity: toFormValue(source.business_identity),
		businessType: toFormValue(source.business_type),
		businessIndustry: toFormValue(source.business_industry),
		placeOfIncorporation: toFormValue(source.incorporation_place),
		businessRegistrationIdType: toFormValue(
			source.business_registration_type
		),
		businessRegistrationId: formatBusinessRegistrationId(
			source.business_registration_id
		),
		businessRegionsOfOperation: toFormValue(source.business_regions),
		businessWebsite: normalizeBusinessWebsite(
			toFormValue(source.business_website)
		),
		address1: toFormValue(source.address_one),
		address2: toFormValue(source.address_two),
		city: toFormValue(source.city),
		stateProvince: toFormValue(source.state),
		postalCode: formatPostalCode(source.postal_code),
		country: toFormValue(source.country),
		primaryBusinessTitle: toFormValue(
			source.primary_admin?.business_title ??
				source.primary_business_title
		),
		primaryJobPosition: toFormValue(
			source.primary_admin?.job_position ??
				source.primary_job_postition ??
				source.primary_job_position
		),
		primaryPhone: toFormValue(
			source.primary_admin?.phone ?? source.primary_phone
		),
	}
}

/**
 * Resolve primary admin fields for organization PATCH payload from GET data.
 *
 * @param {Object} organizationData
 * @return {Object}
 */
const getPrimaryAdminPayloadFields = (organizationData = {}) => {
	const source = {
		...getOrganizationProfile(organizationData),
		...organizationData,
	}
	const admin = source.primary_admin || {}

	return {
		primary_business_title:
			admin.business_title ?? source.primary_business_title ?? null,
		primary_job_position:
			admin.job_position ??
			source.primary_job_position ??
			source.primary_job_postition ??
			null,
		primary_phone: admin.phone ?? source.primary_phone ?? null,
	}
}

/**
 * Map OrganizationSection form values to organization PATCH payload.
 *
 * @param {Object} form
 * @param {String} status
 * @param {Object} [organizationData={}]
 * @return {Object}
 */
const mapFormToOrganizationPayload = (
	form = {},
	status,
	organizationData = {}
) => ({
	business_name: form.legalBusinessName || null,
	business_identity: form.businessIdentity || null,
	business_type: form.businessType || null,
	business_industry: form.businessIndustry || null,
	business_registration_id: toBusinessRegistrationIdPayload(
		form.businessRegistrationId
	),
	business_registration_type: form.businessRegistrationIdType || null,
	business_regions: form.businessRegionsOfOperation || null,
	business_website: normalizeBusinessWebsite(form.businessWebsite) || null,
	incorporation_place: form.placeOfIncorporation || null,
	address_one: form.address1 || null,
	address_two: form.address2 || null,
	city: form.city || null,
	state: form.stateProvince || null,
	postal_code: formatPostalCode(form.postalCode) || null,
	country: form.country || null,
	...getPrimaryAdminPayloadFields(organizationData),
	status,
})

/**
 * Build display name from admin first/last name fields.
 *
 * @param {Object} admin
 * @return {String}
 */
const getOrganizationAdminName = (admin = {}) =>
	[admin.first_name, admin.last_name]
		.filter(Boolean)
		.join(' ')
		.trim()

/**
 * Build admin card meta line: job title and email.
 *
 * @param {Object} admin
 * @param {String} [jobPosition='']
 * @return {String}
 */
const getOrganizationAdminMeta = (admin = {}, jobPosition = '') => {
	const title = String(jobPosition || '').trim()
	const email = String(admin.email || '').trim()

	if (title && email) {
		return `${title} - ${email}`
	}

	return title || email
}

/**
 * Resolve job position for an organization admin card.
 *
 * @param {Object} admin
 * @return {String}
 */
const getOrganizationAdminJobPosition = (admin = {}) =>
	toFormValue(admin.job_position)

/**
 * Map GET organization primary/secondary admin payloads to card display shape.
 *
 * @param {Object} organizationData
 * @return {Array}
 */
const mapOrganizationAdmins = (organizationData = {}) => {
	const profile = getOrganizationProfile(organizationData)
	const source = { ...profile, ...organizationData }
	const admins = []

	if (source.primary_admin) {
		const name = getOrganizationAdminName(source.primary_admin)

		admins.push({
			id: source.primary_admin.id || 'primary-admin',
			name,
			meta: getOrganizationAdminMeta(
				source.primary_admin,
				getOrganizationAdminJobPosition(source.primary_admin)
			),
			roleLabel: ORGANIZATION_STRINGS.adminRole.primary,
			isPrimary: true,
		})
	}

	if (source.secondary_admin) {
		const name = getOrganizationAdminName(source.secondary_admin)

		admins.push({
			id: source.secondary_admin.id || 'secondary-admin',
			name,
			meta: getOrganizationAdminMeta(
				source.secondary_admin,
				getOrganizationAdminJobPosition(source.secondary_admin)
			),
			roleLabel: ORGANIZATION_STRINGS.adminRole.secondaryOptional,
			isPrimary: false,
		})
	}

	return admins
}

/**
 * Empty organization profile form shape.
 *
 * @return {Object}
 */
function getOrganizationFormObject() {
	return {
		profileFriendlyName: '',
		legalBusinessName: '',
		businessIdentity: '',
		businessType: '',
		businessIndustry: '',
		placeOfIncorporation: '',
		businessRegistrationIdType: '',
		businessRegistrationId: '',
		businessRegionsOfOperation: '',
		businessWebsite: '',
		address1: '',
		address2: '',
		city: '',
		stateProvince: '',
		postalCode: '',
		country: '',
		primaryBusinessTitle: '',
		primaryJobPosition: '',
		primaryPhone: '',
	}
}

/**
 * Organization profile section for the new settings layout.
 */
export default {
	components: {
		ClippedText,
		EngageLoader,
		OrganizationProfileStatusBanner,
		OrganizationProfileStatusBadge,
	},

	computed: {
		currentUserName() {
			const { first_name: firstName, last_name: lastName } =
				this.user || {}

			return [firstName, lastName].filter(Boolean).join(' ')
		},

		isFormLocked() {
			return ![
				ORGANIZATION_PROFILE_STATUSES.REJECTED,
				ORGANIZATION_PROFILE_STATUSES.FAILED,
				ORGANIZATION_PROFILE_STATUSES.UNVERIFIED,
				ORGANIZATION_PROFILE_STATUSES.DRAFT,
			].includes(this.profileStatus)
		},

		profileTitleSuffix() {
			return (
				this.organizationDisplayName ||
				this.form.profileFriendlyName ||
				''
			)
		},

		organizationId() {
			const routeOrganizationId = parseInt(
				this.$route.params.organizationId,
				10
			)

			if (this.$route.name === 'app.organization') {
				if (
					Number.isNaN(routeOrganizationId) ||
					!this.isAuthorizedOrganizationId(routeOrganizationId)
				) {
					return null
				}

				return this.roleOrganizationId
			}

			if (!Number.isNaN(routeOrganizationId)) {
				return routeOrganizationId
			}

			return (
				this.roleOrganizationId || this.partner?.organization_id || null
			)
		},

		orgAdmins() {
			return mapOrganizationAdmins(this.organizationProfile || {})
		},

		isSaveDisabled() {
			if (this.isOrganizationSaving || !this.lastSavedSnapshot) {
				return true
			}

			return this.lastSavedSnapshot === this.getCurrentSnapshot()
		},

		...mapState('auth', {
			user: state => state.user,
		}),

		...mapGetters({
			partner: 'partners/active',
			isAuthorizedOrganizationId: 'auth/isAuthorizedOrganizationId',
			organizationProfile: 'settings/organizationProfile',
			organizationProfileStatus: 'settings/organizationProfileStatus',
			organizationDisplayName: 'settings/organizationDisplayName',
			roleOrganizationId: 'auth/organizationId',
		}),
	},

	methods: {
		...mapActions({
			getOrganization: 'settings/getOrganization',
			updateOrganization: 'settings/updateOrganization',
		}),

		getAdminInitials(name) {
			if (!name || typeof name !== 'string') {
				return '?'
			}

			const parts = name
				.trim()
				.split(/\s+/)
				.filter(Boolean)

			if (parts.length === 0) {
				return '?'
			}

			if (parts.length === 1) {
				return parts[0].slice(0, 2).toUpperCase()
			}

			return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
		},

		fillFormFromOrganization(organizationData = null) {
			const data = organizationData || this.organizationProfile || {}
			const profile = getOrganizationProfile(data)

			this.form = {
				...getOrganizationFormObject(),
				...mapOrganizationToForm({
					profile,
					data,
					organizationName: this.organizationName,
				}),
			}
		},

		async loadOrganizationForm() {
			const organizationId = this.organizationId
			if (!organizationId) {
				return
			}

			try {
				this.isOrganizationLoading = true

				const organizationData = await this.getOrganization({
					id: organizationId,
					force: true,
				})
				this.fillFormFromOrganization(
					organizationData || this.organizationProfile
				)
				this.initProfileStatus()
				this.takeSavedSnapshot()
			} finally {
				this.isOrganizationLoading = false
			}
		},

		initProfileStatus() {
			const orgProfile = getOrganizationProfile(
				this.organizationProfile || {}
			)

			this.profileStatus =
				this.organizationProfileStatus ||
				ORGANIZATION_PROFILE_STATUSES.UNVERIFIED
			this.profileStatusMeta = this.buildProfileStatusMeta(orgProfile)
		},

		buildProfileStatusMeta(orgProfile = {}) {
			return {
				savedAt: orgProfile.draft_at || null,
				savedBy: orgProfile.draft_by_name || null,
				submittedAt: orgProfile.submitted_at || null,
				submittedBy: orgProfile.submitted_by_name || null,
				verifiedAt: orgProfile.verified_at || null,
				rejectionMessage: orgProfile.rejected_reason || '',
				failureMessage: orgProfile.failed_reason || '',
			}
		},

		isOrganizationFieldValueLocked(field) {
			const isEditableStatus = [
				ORGANIZATION_PROFILE_STATUSES.DRAFT,
				ORGANIZATION_PROFILE_STATUSES.UNVERIFIED,
			].includes(this.profileStatus)

			if (isEditableStatus) {
				return false
			}

			if (!this.lastSavedSnapshot) {
				return Boolean(this.form[field])
			}

			const savedForm = JSON.parse(this.lastSavedSnapshot)

			return Boolean(savedForm[field])
		},

		clearOrganizationField(field) {
			if (Object.prototype.hasOwnProperty.call(this.form, field)) {
				this.form[field] = ''
			}

			if (field === 'businessWebsite') {
				this.businessWebsiteErrorMessage = ''
			}
		},

		onBusinessWebsiteBlur() {
			this.form.businessWebsite = normalizeBusinessWebsite(
				this.form.businessWebsite
			)
			this.validateBusinessWebsite()
		},

		validateBusinessWebsite() {
			if (isValidBusinessWebsite(this.form.businessWebsite)) {
				this.businessWebsiteErrorMessage = ''
				return true
			}

			this.businessWebsiteErrorMessage = this.validation.websiteInvalid
			return false
		},

		onBusinessRegistrationIdInput() {
			this.form.businessRegistrationId = formatBusinessRegistrationId(
				this.form.businessRegistrationId
			)
		},

		onPostalCodeInput() {
			this.form.postalCode = formatPostalCode(this.form.postalCode)
		},

		getCurrentSnapshot() {
			return JSON.stringify(this.form)
		},

		takeSavedSnapshot() {
			this.lastSavedSnapshot = this.getCurrentSnapshot()
		},

		restoreFromSnapshot() {
			if (!this.lastSavedSnapshot) {
				return
			}

			this.form = {
				...getOrganizationFormObject(),
				...JSON.parse(this.lastSavedSnapshot),
			}
			this.businessWebsiteErrorMessage = ''
		},

		startEditing() {
			if (this.isFormLocked) {
				return
			}

			this.isEditing = true
		},

		cancelEditing() {
			this.restoreFromSnapshot()
			this.isEditing = false
		},

		buildOrganizationPayload(status) {
			return mapFormToOrganizationPayload(
				this.form,
				status,
				this.organizationProfile
			)
		},

		async saveOrganization(status) {
			const organizationId = this.organizationId
			if (!organizationId || this.isOrganizationSaving) {
				return null
			}

			this.form.businessWebsite = normalizeBusinessWebsite(
				this.form.businessWebsite
			)

			if (!this.validateBusinessWebsite()) {
				return null
			}

			const preservedForm = { ...this.form }
			const wasEditing = this.isEditing

			this.isOrganizationSaving = true

			try {
				await this.updateOrganization({
					id: organizationId,
					payload: this.buildOrganizationPayload(status),
				})

				if (status !== ORGANIZATION_PROFILE_STATUSES.DRAFT) {
					this.fillFormFromOrganization(this.organizationProfile)
				}

				this.initProfileStatus()
				this.takeSavedSnapshot()
				return this.organizationProfile
			} catch (error) {
				this.form = {
					...getOrganizationFormObject(),
					...preservedForm,
				}
				this.isEditing = wasEditing
				throw error
			} finally {
				this.isOrganizationSaving = false
			}
		},

		async onSaveDraft() {
			if (this.isSaveDisabled) {
				return
			}

			const preservedForm = { ...this.form }

			try {
				const savedProfile = await this.saveOrganization(
					ORGANIZATION_PROFILE_STATUSES.DRAFT
				)

				if (!savedProfile) {
					return
				}

				this.isEditing = false
				this.profileStatus = ORGANIZATION_PROFILE_STATUSES.DRAFT
				this.profileStatusMeta = {
					savedAt: moment().toISOString(),
					savedBy: this.currentUserName || '',
				}
				this.$alert.success(this.strings.saveDraftSuccess)
			} catch (error) {
				this.form = {
					...getOrganizationFormObject(),
					...preservedForm,
				}
				this.isEditing = true
				this.$alert.error(
					error.response?.message ||
						'Failed to save organization draft.'
				)
			}
		},

		async onSubmitForApproval() {
			if (this.isOrganizationSaving) {
				return
			}

			if (hasEmptyRequiredOrganizationFields(this.form)) {
				this.$alert.error(this.strings.submitIncomplete)
				return
			}

			try {
				const savedProfile = await this.saveOrganization(
					ORGANIZATION_PROFILE_STATUSES.PENDING
				)

				if (!savedProfile) {
					return
				}

				this.isEditing = false
				this.profileStatus =
					ORGANIZATION_PROFILE_STATUSES.PENDING_REVIEW
				this.profileStatusMeta = {
					submittedAt: moment().toISOString(),
				}
				this.$alert.success(this.strings.submitSuccess)
			} catch (error) {
				this.$alert.error(
					error.response?.message ||
						'Failed to submit organization for approval.'
				)
			}
		},

		cancelAllEditing() {
			if (this.isEditing) {
				this.cancelEditing()
			}
		},
	},

	name: 'OrganizationSection',

	props: {
		organizationName: {
			type: String,
			default: '',
		},
	},

	watch: {
		organizationId: {
			handler(organizationId) {
				if (organizationId) {
					this.loadOrganizationForm()
				}
			},
			immediate: true,
		},

		organizationProfile(profile) {
			if (
				!profile ||
				this.isEditing ||
				this.isOrganizationSaving ||
				this.isOrganizationLoading
			) {
				return
			}

			this.fillFormFromOrganization(profile)
			this.initProfileStatus()
			this.takeSavedSnapshot()
		},
	},

	activated() {
		if (this.organizationId) {
			this.loadOrganizationForm()
		}
	},

	beforeDestroy() {
		this.cancelAllEditing()
	},

	deactivated() {
		this.cancelAllEditing()
	},

	data() {
		return {
			aria: SETTINGS_ARIA,
			common: SETTINGS_COMMON,
			form: getOrganizationFormObject(),
			isEditing: false,
			isOrganizationLoading: false,
			isOrganizationSaving: false,
			lastSavedSnapshot: null,
			placeholders: SETTINGS_PLACEHOLDERS,
			profileStatus: ORGANIZATION_PROFILE_STATUSES.UNVERIFIED,
			profileStatusMeta: {},
			stateProvinceOptions: ORGANIZATION_STATE_PROVINCE_OPTIONS,
			strings: ORGANIZATION_STRINGS,
			validation: SETTINGS_VALIDATION,
			businessWebsiteErrorMessage: '',
		}
	},
}
</script>
